import * as React from "react";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

type InputHTMLAttributesWithoutSize = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">;

export interface InputProps
  extends InputHTMLAttributesWithoutSize, VariantProps<typeof inputVariants> {
  asChild?: boolean;
}

const inputVariants = cva(
  "flex w-full rounded-lg border border-input bg-background px-3 py-2 text-base font-medium ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&[type=number]]:appearance-textfield",
  {
    variants: {
      size: {
        default: "h-11",
        // sm intentionally matches default — uniform form-input height for
        // composed controls (phoneInput, time-input). Don't diverge without
        // checking their alignment with adjacent h-11 fields.
        sm: "h-11",
        lg: "h-14",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, size, type, onWheel, ...props }, ref) => {
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === "number") {
        e.currentTarget.blur();
      }
      onWheel?.(e);
    };

    return (
      <input
        type={type}
        className={cn(inputVariants({ size, className }))}
        ref={ref}
        onWheel={handleWheel}
        {...(props.value !== undefined ? { value: props.value ?? "" } : {})}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
